import { assign, createMachine, sendParent } from 'xstate';
import { NavigationSections } from '../constants';
import { CustomerFlowType } from '../constants/customer-flow-type';
import { ApplyTypes, CustomerEligibility, FormTypes, GeneralEligiblilityCodes } from '../gql/graphql';
import checkCode from '../networkRequests/OTP/checkVerificationToken';
import sendCode from '../networkRequests/OTP/sendVerificationToken';
import createOrder from '../networkRequests/order/createOrder';
import { buildAnalytics } from '../helpers/analytics/buildAnalytics';
import { getNewOrderDetailsFromOrderDetails } from '../helpers/converters/getNewOrderDetailsFromOrderDetails';
import verifyEmail from '../networkRequests/basicInfo/verifyEmail';
import queryCustomersByCellphone from '../networkRequests/customerSearch/queryCustomersByCellphone';
import queryCustomersByIDV from '../networkRequests/customerSearch/queryCustomersByIDV';
import { PropagateNetworkErrors, sharedActions } from './actions/genericActions';
import { identify, page, track } from './actions/segment';
import { trackEmailValidationError } from './actions/trackEmailValidationError';
import { AssignCustomerSearchResult } from './actions/sharedNetworkActions';
import {
  CustomerLookupEvents,
  CustomerLookupMachineContext,
  CustomerLookupMachineEvents,
  CustomerLookupMachineGuards,
  CustomerLookupMachineStates,
  CustomerLookupStates,
  STARTING_PAGE_OTP_BYPASS,
} from './types/customerLookupMachine.types';
import { BasicInfoContinuedModel, EcommMachineEvents } from './types/ecommMachine.types';
import { MakeStateCallingQueryOrderPreAuth } from './refresh-order';

export default createMachine<CustomerLookupMachineContext, CustomerLookupMachineEvents, CustomerLookupMachineStates>(
  {
    id: 'CustomerLookup',
    initial: CustomerLookupStates.CustLookupInitialState,
    context: {} as CustomerLookupMachineContext, // Nothing you put here matters, the init block is the data in the `src: CustomerLookupMachine` line in ecomm-machine
    on: {
      [CustomerLookupEvents.TRACK]: {
        actions: (context, event: any) => track(event?.data?.eventName, event?.data?.payload)(context, event),
      },
      [CustomerLookupEvents.PAGE]: {
        actions: (context, event: any) => page(event?.data?.pageName, event?.data?.payload)(context, event),
      },
      [CustomerLookupEvents.IDENTIFY]: {
        actions: (context, event: any) => identify(event?.data?.customerSorId, event?.data?.payload),
      },
    },
    states: {
      [CustomerLookupStates.CustLookupInitialState]: {
        always: [
          { cond: 'Initial', target: CustomerLookupStates.OTPRequest },
          { cond: 'SkipToBasics', target: CustomerLookupStates.BasicInfo },
          { cond: 'SkipToBasicsContinued', target: CustomerLookupStates.BasicsContinued },
          { cond: 'SkipToOTPCheck', target: '#CustomerLookup.OTPRequest.callingSendCode' },
          { cond: 'SkipOTP', target: '#CustomerLookup.OTPRequest.callingQueryOrderPreAuthFromSkipOTP' },
        ],
      },
      [CustomerLookupStates.OTPRequest]: {
        initial: 'waiting',
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
        },
        states: {
          waiting: {
            on: {
              [CustomerLookupEvents.SEND_TOKEN]: {
                target: 'callingSendCode',
                actions: [assign({ cellPhone: (_, event: any) => event?.data?.cellPhone!, sendCodeErrors: [], skipToPage: 'OTPCheck' })],
              },
              [CustomerLookupEvents.SKIP_OTP]: {
                target: 'callingQueryOrderPreAuthFromSkipOTP',
                actions: [assign({ cellPhone: (_, event) => event?.data?.cellPhone!, didSkipOTP: true })],
              },
            },
          },
          callingSendCode: {
            tags: 'calling',
            invoke: {
              src: (context) => sendCode(context.cellPhone),
              onDone: [
                {
                  cond: 'HasSendCodeFatalError',
                  target: '#CustomerLookup.InternalError',
                  actions: [assign({ sendCodeErrors: (_, event) => event.data.sendTokenErrors }), track('Internal Error Occurred - OTP Request')],
                },
                {
                  cond: 'HasOTPErrors',
                  target: 'waiting',
                  actions: assign({ sendCodeErrors: (_, event) => event.data.sendTokenErrors }),
                },
                {
                  cond: 'IsOTPSsent',
                  target: 'callingCustomerSearch',
                },
                { target: 'waiting' },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
          callingCustomerSearch: {
            tags: 'calling',
            invoke: {
              src: queryCustomersByCellphone,
              onDone: [
                { cond: 'SkipToOTPCheck', target: '#CustomerLookup.OTPCheck', actions: AssignCustomerSearchResult },
                { cond: 'CustomerOTB', target: '#CustomerLookup.OTB', actions: AssignCustomerSearchResult },
                { cond: 'ExistingCustomerFound', target: '#CustomerLookup.Last4SSN', actions: AssignCustomerSearchResult },
                { cond: 'IsLongApp', target: '#CustomerLookup.LongFormApplication', actions: AssignCustomerSearchResult }, // starting a new long form app
                { cond: 'CustomerNotFound', target: '#CustomerLookup.BasicInfo', actions: AssignCustomerSearchResult },

                // this default could technically could happen if we return VerifiedCustomer,
                // however that can't practically happen since that requires last4 to be passed into customer search
                { actions: [AssignCustomerSearchResult, 'log'] }, // should log stay here permanently?
              ],
              onError: [
                {
                  cond: (context, event) => !!event.data?.forbiddenError && !context.hasRetriedWithNewOrder,
                  target: `${CustomerLookupStates.CreatingNewOrder}`,
                },
                { actions: PropagateNetworkErrors },
              ],
            },
          },
          [CustomerLookupStates.CreatingNewOrder]: {
            tags: 'calling',
            invoke: {
              // OR-3290: the session moves to this new order, so it has to keep the cart and customer
              // details the integration put on the original one or a Shop First checkout turns into
              // an empty-cart (Apply First) pre-approval.
              src: (context) =>
                createOrder({
                  analytics: buildAnalytics(),
                  details: getNewOrderDetailsFromOrderDetails(context.orderDetails, context.cellPhone),
                }),
              onDone: {
                target: 'callingCustomerSearch',
                actions: [
                  assign({
                    hasRetriedWithNewOrder: true,
                    orderId: (_, event) => {
                      const newOrderId = event.data?.data?.createOrder?.order?.id;
                      if (newOrderId) {
                        const url = new URL(window.location.href);
                        url.searchParams.set('orderId', newOrderId);
                        url.searchParams.delete('appStatus');
                        window.history.replaceState({}, '', url.toString());
                      }
                      return newOrderId;
                    },
                  }),
                  track('New Order Created - Session Associated With Different Phone Number'),
                ],
              },
              onError: { actions: PropagateNetworkErrors },
            },
          },
          callingQueryOrderPreAuthFromSkipOTP: MakeStateCallingQueryOrderPreAuth('callingCustomerSearch'),
        },
      },
      [CustomerLookupStates.OTPCheck]: {
        initial: CustomerLookupStates.CheckCode,
        on: {
          [CustomerLookupEvents.BACK]: {
            target: CustomerLookupStates.OTPRequest,
          },
        },
        meta: {
          navigationSection: NavigationSections.HIDE_PROGRESS_BAR,
          canGoBackViaHeader: true,
        },
        states: {
          [CustomerLookupStates.CheckCode]: {
            initial: 'waiting',
            states: {
              waiting: {
                on: {
                  [CustomerLookupEvents.CHECK_OTP]: {
                    target: 'calling',
                    actions: assign({
                      checkCodeErrors: [],
                      selectedOptIntoSmsMarketing: (_, event) => event.data?.selectedOptIntoSmsMarketing,
                    }),
                  },
                },
              },
              calling: {
                tags: 'calling',
                invoke: {
                  src: checkCode,
                  onDone: [
                    {
                      cond: (_, event) => !event.data?.checkTokenErrors?.length,
                      target: '#CustomerLookup.OTPCheck.CustomerSearch',
                      actions: [track('Mobile Number Confirmed')],
                    },
                    { target: 'waiting', actions: assign({ checkCodeErrors: (_, event) => event.data.checkTokenErrors }) }, // assumed else has errors
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
            },
          },
          // Tom doesn't think this does anything anymore because customer search returns everything that query order does.
          // Also if it were to work, it would have to be after queryCustomersByCellphone
          // [CustomerLookupStates.QueryOrder]: MakeStateCallingQueryOrderPreAuth(CustomerLookupStates.CustomerSearch),
          [CustomerLookupStates.CustomerSearch]: {
            always: [
              {
                cond: 'CustomerOTB',
                target: '#CustomerLookup.OTB',
              },
              {
                cond: 'ExistingCustomerFound',
                target: '#CustomerLookup.Last4SSN',
              },
              {
                cond: 'IsLongApp',
                target: '#CustomerLookup.LongFormApplication',
              }, // starting a new long form app
              {
                cond: 'CustomerNotFound',
                target: '#CustomerLookup.BasicInfo',
              },

              // this default could technically could happen if we return VerifiedCustomer,
              // however that can't practically happen since that requires last4 to be passed into customer search
              { actions: 'log' }, // should log stay here permanently?
            ],
          },
        },
      },
      [CustomerLookupStates.Last4SSN]: {
        initial: CustomerLookupStates.CustomerSearch,
        states: {
          [CustomerLookupStates.CustomerSearch]: {
            initial: 'waiting',
            states: {
              waiting: { on: { [CustomerLookupEvents.LAST4SSN]: [{ target: 'callingSearch' }] } }, // Don't need the endsWith last 4 check because that is OTB only
              callingSearch: {
                tags: 'calling',
                invoke: {
                  src: queryCustomersByIDV,
                  onDone: [
                    /*
                    Process and redirect in this order:
                    1. App in progress
                    2. OTB 
                    3. Errors and ineligibles 
                    4. New application (type of new application handled by ephemeral state)
                     */
                    {
                      cond: 'HasInProgressApp',
                      target: CustomerLookupStates.FoundInProgressApp,
                      actions: [AssignCustomerSearchResult, track('Passed eligibility')],
                    },
                    {
                      cond: 'CustomerOTB',
                      target: '#CustomerLookup.OTB',
                      actions: [
                        AssignCustomerSearchResult,
                        assign({ otbInitialState: 'OTB_APPLY' }),
                        track('Passed eligibility'),
                        track('Customer OTB Eligible'),
                      ],
                    },
                    {
                      cond: 'CustomerSearchNotFound',
                      target: 'waiting', // This uses the `We can't find your account` subpage in the last4 page
                      actions: [AssignCustomerSearchResult, assign({ isLast4Different: true })],
                    },
                    { cond: 'CustomerSearchFatalError', target: '#CustomerLookup.InternalError', actions: AssignCustomerSearchResult },
                    {
                      cond: 'UnauthorizedDealerResolverError',
                      target: '#CustomerLookup.Ineligible',
                      actions: [
                        AssignCustomerSearchResult,
                        assign({
                          // used as a hacky approach to get the ineligible page to display what we want
                          eligibilityDetails: (context) =>
                            ({
                              ...context.eligibilityDetails,
                              eligibilityCode: GeneralEligiblilityCodes.AppInProgress,
                            } as CustomerEligibility),
                        }),
                      ],
                    },
                    {
                      cond: 'DocsSignedAppInProgressResolverError',
                      target: '#CustomerLookup.Ineligible',
                      actions: [
                        AssignCustomerSearchResult,
                        assign({
                          eligibilityDetails: (context) =>
                            ({
                              ...context.eligibilityDetails,
                              eligibilityCode: GeneralEligiblilityCodes.AppInProgress,
                            } as CustomerEligibility),
                        }),
                      ],
                    },
                    {
                      // This is the case for other resolver errors
                      // Delete this message when read: Tom deleted this condition 'LTO Services Failure: Unable to add items to application'
                      // because it has not triggered in the last 15 months. It previously used to re-query order and that shouldn't change anything.
                      // See link below (need to copy paste entire link)
                      // https://dd9e94bcca9f405bbdc68e23c6d7cdce.us-east-1.aws.found.io:9243/app/discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:now-15M,to:now))&_a=(columns:!(message,error.message,location),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'0d4f1bb0-d675-11ee-abef-c91ed9643a54',key:_index,negate:!f,params:(query:prod-integrations-application-edge-service-000034),type:phrase),query:(match_phrase:(_index:prod-integrations-application-edge-service-000034))),('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'0d4f1bb0-d675-11ee-abef-c91ed9643a54',key:log.origin.file.name,negate:!t,params:(query:validating%2Fvalidate.go),type:phrase),query:(match_phrase:(log.origin.file.name:validating%2Fvalidate.go)))),index:'0d4f1bb0-d675-11ee-abef-c91ed9643a54',interval:auto,query:(language:kuery,query:'%22could%20not%20update%20application%20with%20the%20items%22'),sort:!(!('@timestamp',desc)))
                      cond: (context, event) =>
                        !!event.data?.resolverErrors?.length && event.data?.resolverErrors[0].message !== 'customer is not authorized to retrieve application',
                      target: '#CustomerLookup.Ineligible',
                      actions: AssignCustomerSearchResult,
                    },
                    { cond: 'Ineligible', target: '#CustomerLookup.Ineligible', actions: AssignCustomerSearchResult },
                    { cond: 'OpenLineTooSmall', target: '#CustomerLookup.Ineligible', actions: AssignCustomerSearchResult },
                    { target: '#CustomerLookup.Last4SSN.StartNewApplication', actions: AssignCustomerSearchResult },
                  ],
                  onError: { actions: PropagateNetworkErrors },
                },
              },
              // This is a select state to increase readability, it does not map to a page
              [CustomerLookupStates.FoundInProgressApp]: {
                always: [
                  { cond: 'IsPostApproval', target: '#CustomerLookup.ExistingApplication' },
                  { cond: 'IsLongApp', target: '#CustomerLookup.LongFormApplication' },
                  { cond: 'IsDraftShortApp', target: '#CustomerLookup.BasicInfo' },
                  { cond: 'LeaseWithPreapprovalError', target: '#CustomerLookup.AccommodationAddressErrors' },
                  { target: '#CustomerLookup.ExistingApplication' },
                ],
              },
            },
          },
          // This is just a redirect state, that is targeted by multiple transitions
          [CustomerLookupStates.StartNewApplication]: {
            always: [{ cond: 'IsLongApp', target: '#CustomerLookup.LongFormApplication' }, { target: '#CustomerLookup.BasicInfo' }],
          },
        },

        on: {
          // from clicking the Start New Application button
          [CustomerLookupEvents.NOT_RECOGNIZED]: {
            target: '#CustomerLookup.Last4SSN.StartNewApplication',
            actions: assign({
              customers: [],
              customerFlowType: CustomerFlowType.NewCustomer,
            }),
          },
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
        },
      },
      [CustomerLookupStates.ExistingApplication]: {
        type: 'final',
        entry: [
          sendParent((context, event: any) => ({
            data: { ...context },
            type: EcommMachineEvents.EXISTING_APPLICATION,
          })),
        ],
      },
      [CustomerLookupStates.OTB]: {
        type: 'final',
        entry: [
          sendParent((context, event) => ({
            data: { ...context },
            type: EcommMachineEvents.OTB_CUSTOMER,
          })),
        ],
      },
      [CustomerLookupStates.BasicInfo]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [CustomerLookupEvents.NEXT]: {
                target: 'callingVerifyEmail',
                actions: assign({ verifyEmailErrors: [], basicInfo: (_, event) => event.data }),
              },
            },
          },
          callingVerifyEmail: {
            tags: 'calling',
            invoke: {
              id: 'verifyEmail',
              src: (_, event) => verifyEmail(event.data?.emailAddress!),
              onDone: [
                {
                  cond: (_, event) => !!event.data?.length,
                  target: 'waiting',
                  actions: [assign({ verifyEmailErrors: (_, event) => event.data }), trackEmailValidationError],
                },
                {
                  target: '#CustomerLookup.BasicsContinued',
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
        },
      },
      [CustomerLookupStates.BasicsContinued]: {
        initial: 'waiting',
        states: {
          waiting: {
            on: {
              [CustomerLookupEvents.SUBMIT_BASICS_CONTINUED]: [
                {
                  cond: 'CustomerNewOrChangedSsnDob',
                  actions: assign({
                    basicInfoContinued: (_context, event: any) => event.data?.basicInfoContinued as BasicInfoContinuedModel,
                    didCustomerChange: (_context, event: any) => !!event.data?.didCustomerChange,
                  }),
                  target: 'callingSearch',
                },
                {
                  type: 'final',
                  actions: sendParent((context, event) => ({
                    data: { ...context, ...event.data },
                    type: EcommMachineEvents.NEW_APPLICATION,
                  })),
                },
              ],
            },
          },
          callingSearch: {
            tags: 'calling',
            invoke: {
              src: queryCustomersByIDV,
              onDone: [
                {
                  cond: 'CustomerNotFound',
                  type: 'final',
                  actions: [
                    track('SSN DOB ROE response', { response: 'new customer' }),
                    AssignCustomerSearchResult,
                    sendParent((context) => ({
                      type: EcommMachineEvents.NEW_APPLICATION,
                      data: { ...context },
                    })),
                  ],
                },
                {
                  cond: 'IsIneligible',
                  target: '#CustomerLookup.Ineligible',
                  actions: [track('SSN DOB ROE response', { response: 'returning - ineligible' }), AssignCustomerSearchResult],
                },
                {
                  // This guard catches the `customer is ineligible` resolver error.
                  cond: 'IsIneligibleResolverError',
                  target: '#CustomerLookup.Ineligible',
                  actions: [track('SSN DOB ROE response', { response: 'returning - ineligible' }), AssignCustomerSearchResult],
                },
                {
                  //not sure why but when returning customer with different phone number has an app in docsSigned, the eligibiltyDetails come back as null
                  //so this separate gaurd:
                  // 1. finds the particular resolver error
                  // 2. then sets the eligibilityCode to appInProgress manually when this edge case occurs
                  cond: 'DocsSignedAppInProgressResolverError',
                  target: '#CustomerLookup.Ineligible',
                  actions: [
                    AssignCustomerSearchResult,
                    assign({
                      eligibilityDetails: (context) =>
                        ({
                          ...context.eligibilityDetails,
                          eligibilityCode: GeneralEligiblilityCodes.AppInProgress,
                        } as CustomerEligibility),
                    }),
                  ],
                },
                {
                  cond: 'UnauthorizedDealerResolverError',
                  target: '#CustomerLookup.Ineligible',
                  actions: [
                    AssignCustomerSearchResult,
                    assign({
                      // used as a hacky approach to get the ineligible page to display what we want
                      eligibilityDetails: (context) =>
                        ({
                          ...context.eligibilityDetails,
                          eligibilityCode: GeneralEligiblilityCodes.AppInProgress,
                        } as CustomerEligibility),
                    }),
                  ],
                },
                /**
                 * For OTB eligible customers, do not allow updates to contact information within the application flow to prevent fraud.
                 * This is a temporary measure until email verification can be implemented
                 * The cellphone changing is a major fraud indicator, which presents as a different phone number, or a skipped OTP
                 */
                {
                  target: '#CustomerLookup.PhoneNumberChanged',
                  cond: (context, event) =>
                    event?.data?.eligibilityDetails?.applyTypes[0] === ApplyTypes.LeaseOtb &&
                    (context.cellPhone !== event?.data?.customers[0]?.cellPhone || context.didSkipOTP),
                  actions: [
                    (context, event) => track('Contact Info Mismatch', { customerSorId: event?.data?.customers[0]?.id })(context, event),
                    AssignCustomerSearchResult,
                  ],
                },
                /**
                 * If we find that the customer is OTB eligible after collecting date of birth and full SSN, the OTB machine will be invoked.
                 * This means that they will immediately see the SSN last four verification screen.  This was a conscious desision to satisfy
                 * compliance requirements around providing appropriate disclosures before application submission.  This isn't really the cleanest
                 * experience, so consider revisiting this approach in the future.
                 */
                {
                  cond: 'CustomerOTB',
                  target: '#CustomerLookup.OTB',
                  actions: [track('SSN DOB ROE response', { response: 'returning - OTB eligible' }), AssignCustomerSearchResult],
                },
                {
                  type: 'final',
                  actions: [
                    track('SSN DOB ROE response', { response: 'returning - new app' }),
                    AssignCustomerSearchResult,
                    sendParent((context) => ({
                      type: EcommMachineEvents.NEW_APPLICATION,
                      data: { ...context },
                    })),
                  ],
                },
              ],
              onError: { actions: PropagateNetworkErrors },
            },
          },
        },
        on: {
          [CustomerLookupEvents.BACK]: {
            target: '#CustomerLookup.BasicInfo',
          },
        },
        meta: {
          navigationSection: NavigationSections.CHECK_ELIGIBILITY,
          canGoBackViaHeader: true,
        },
      },
      [CustomerLookupStates.Decline]: {},
      [CustomerLookupStates.LongFormApplication]: {
        type: 'final',
        entry: sendParent((context, event) => ({
          data: { ...context },
          type: EcommMachineEvents.LONG_FORM,
        })),
      },
      [CustomerLookupStates.InternalError]: {},
      [CustomerLookupStates.AccommodationAddressErrors]: {
        on: {
          [CustomerLookupEvents.BACK]: [
            {
              cond: 'LeaseWithSsnDeclineReason',
              target: CustomerLookupStates.BasicsContinued,
            },
            {
              cond: 'LeaseWithAddressDeclineReason',
              actions: sendParent((context, event) => ({
                type: EcommMachineEvents.NEW_APPLICATION,
                data: { ...context },
              })),
              type: 'final',
            },
          ],
        },
      },
      [CustomerLookupStates.Ineligible]: {},
      [CustomerLookupStates.PhoneNumberChanged]: {
        on: {
          [EcommMachineEvents.RESTART_APPLICATION]: {
            actions: assign({
              cellPhone: '',
              basicInfo: undefined,
              basicInfoContinued: undefined,
              accommodationAddress: undefined,
              billingAddress: undefined,
            }),
            target: CustomerLookupStates.OTPRequest,
          },
        },
      },
    },
    schema: {
      context: {} as CustomerLookupMachineContext,
      events: {} as CustomerLookupMachineEvents,
      guards: {} as CustomerLookupMachineGuards,
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    actions: {
      ...sharedActions,
    },

    guards: {
      ExistingCustomerFound: (_context, event) => {
        const flowType = event.data?.customerFlowType ?? _context.customerFlowType;
        return flowType === CustomerFlowType.SingleCustomer || flowType === CustomerFlowType.MultipleCustomers;
      },
      CustomerOTB: (context, event) => {
        const applyType = event.data?.eligibilityDetails?.applyTypes[0] ?? context.eligibilityDetails?.applyTypes?.[0];
        return applyType === ApplyTypes.LeaseOtb || applyType === ApplyTypes.LoanOtb;
      },
      CustomerNotFound: (_context, event) => {
        const flowType = event.data?.customerFlowType ?? _context.customerFlowType;
        return flowType === CustomerFlowType.NewCustomer;
      },
      SkipToBasicsContinued: (context, event) => {
        return context.skipToPage === 'BasicInfoContinued';
      },
      SkipToBasics: (context, event) => {
        return context.skipToPage === 'BasicInfo';
      },
      SkipToOTPCheck: (context, event) => {
        return context.skipToPage === 'OTPCheck';
      },
      SkipOTP: (context) => {
        return context.skipToPage === STARTING_PAGE_OTP_BYPASS;
      },
      Initial: (context, event) => {
        return !context.skipToPage;
      },
      IsPostApproval: (context, _) => {
        // used to stop resumed apps that are already approved from reapplying
        // this should technically need a b&m check, however the default case for the block covers it
        return (
          context?.application?.lease?.status === 'approved' ||
          context?.application?.lease?.status === 'docsReady' ||
          context?.application?.lease?.status === 'docsSigned'
        );
      },
      IsDraftShortApp: (context, _) => {
        return context?.application?.lease?.status === 'draft' && context?.dealer?.formType === FormTypes.Short;
      },
      IsLongApp: (context, _event) => {
        return context.dealer?.formType === FormTypes.Long || context.dealer?.formType === FormTypes.LongV2;
      },
      LeaseWithPreapprovalError: (context, _) => {
        return context?.application?.lease?.status === 'errorsFound' && ['address', 'ssn'].includes(context?.application?.lease?.declineReason || '');
      },
      LeaseWithSsnDeclineReason: (context, event) => {
        const declineReason = context.application?.lease?.declineReason;
        return declineReason === 'ssn';
      },
      LeaseWithAddressDeclineReason: (context, event) => {
        const declineReason = context.application?.lease?.declineReason;
        return declineReason === 'address';
      },
      IsIneligible: (_context, event: any) => {
        return event?.data?.eligibilityDetails?.isEligible === false || event.data?.eligibilityDetails?.otbCode === 4;
      },
      CustomerNewOrChangedSsnDob: (context, event: any) => {
        return !!event.data?.didCustomerChange;
      },
      // ~~~~~~~ GUARDS FOR REFACTOR ~~~~~~~ //
      // CustomerSearch by last4 guards
      CustomerSearchNotFound: (_, event) => !!event.data?.customerSearchErrors?.length && event.data?.customerSearchErrors[0].__typename === 'NotFound',
      CustomerSearchFatalError: (_, event) => !!event.data?.customerSearchErrors?.length && event.data?.customerSearchErrors[0].__typename === 'FatalError',
      UnauthorizedDealerResolverError: (_, event) => !!event.data?.resolverErrors?.length && event.data?.resolverErrors[0].message === 'unauthorized dealer',
      DocsSignedAppInProgressResolverError: (_, event) =>
        !!event.data?.resolverErrors?.length &&
        event.data?.resolverErrors[0].message === "Applicant has an application in 'Docs Signed'. Not eligibile for new app at this time",
      IsIneligibleResolverError: (_, event) =>
        !!event.data?.resolverErrors?.length && event.data?.resolverErrors.some((err: any) => err.message === 'customer is ineligible'),
      Ineligible: (_, event) =>
        !!event.data?.eligibilityDetails &&
        event.data?.eligibilityDetails?.application === null &&
        !(!!event.data?.resolverErrors?.length && event.data?.resolverErrors[0].message === 'customer is not authorized to retrieve application') &&
        !event.data?.eligibilityDetails?.isEligible,
      OpenLineTooSmall: (_, event) =>
        !!event.data?.eligibilityDetails &&
        event.data?.eligibilityDetails?.application === null &&
        !(!!event.data?.resolverErrors?.length && event.data?.resolverErrors[0].message === 'customer is not authorized to retrieve application') &&
        event.data?.eligibilityDetails?.eligibilityCode === GeneralEligiblilityCodes.Eligibile &&
        event.data?.eligibilityDetails.otbCode === 4,
      // FatalError: (context, event) => {
      //   if (event.data && event.data.length > 0) {
      //     return event.data[0].__typename === 'FatalError';
      //   }
      //   return false;
      // },

      IsEligibleVerifiedCustomer: (context, event: any) => {
        // technically draft apps need to be excluded here because they don't fail eligibility, however that is handled by the order
        return !!event.data?.eligibilityDetails?.isEligible && event?.data?.customerFlowType === CustomerFlowType.VerifiedCustomer;
      },
      IsOTPSsent: (_context, event) => {
        // event = event as SendVerificationTokenEvent;
        return event.data?.status === 'pending';
      },
      HasOTPErrors: (_context, event) => {
        // event = event as SendVerificationTokenEvent;
        return !!event.data?.sendTokenErrors && event.data?.sendTokenErrors?.length > 0;
      },
      HasSendCodeFatalError: (_context, event) => {
        // event = event as SendVerificationTokenEvent;
        return !!event.data?.sendTokenErrors?.length && event.data?.sendTokenErrors.some((error) => error.__typename === 'FatalError');
      },
      HasInProgressApp: (_context, event: any) => {
        return (
          (!!event.data?.application?.loan?.id && validLoanAppStatuses.includes(event.data?.application?.loan!.status)) ||
          (!!event.data?.application?.lease?.id && validLeaseAppStatuses.includes(event.data?.application?.lease!.status))
        );
      },
    },
  },
);

// TODO: move me
// these are the statuses that can be resumed
const validLeaseAppStatuses = ['draft', 'approved', 'preApproved', 'docsSigned', 'docsReady', 'errorsFound']; // TODO errorsFound needs to be exlcuded from this list and handled separately
const validLoanAppStatuses = ['approved', 'accepted', 'docs_ready', 'errorsFound', 'delivered'];
